import * as moment from 'moment';
import { useEffect, useState } from 'react';
import { Button, Glyphicon } from 'react-bootstrap';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import useNetworkEmailLogFilteredList, { NetworkEmailLogSortColumn } from '@nkr/store/network-email-log/hooks/use-network-email-log-filtered-list';
import useNetworkEmailLogSenders from '@nkr/store/network-email-log/hooks/use-network-email-log-senders';
import NetworkEmailDetailDrawer from './detail-drawer';
import {
    Card, ContactLink, Ellipsis, EmptyRow, Footer, Page, PageButton, Pager, SearchBox, SenderSelect,
    SentDate, SortButton, SubjectLink, Table, Title, TitleBar, Toolbar, ViewButton, styles
} from './styles';

const pageSize = 15;

const getPageNumbers = (current: number, totalPages: number): (number | null)[] => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 3) {
        return [1, 2, 3, null, totalPages];
    }

    if (current >= totalPages - 2) {
        return [1, null, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, null, current - 1, current, current + 1, null, totalPages];
};

interface IProps {
    centerId?: number;
}

const NetworkEmailSentList = ({ centerId }: IProps) => {
    const [sender, setSender] = useState<string>('');
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [sortBy, setSortBy] = useState<NetworkEmailLogSortColumn>('date');
    const [sortDescending, setSortDescending] = useState<boolean>(true);
    const [page, setPage] = useState<number>(1);
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const senders = useNetworkEmailLogSenders(centerId);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const { loading, error, items, total } = useNetworkEmailLogFilteredList({
        sender: sender || undefined,
        search: debouncedSearch || undefined,
        centerId,
        sortBy,
        sortDescending,
        page,
        pageSize
    });

    const totalPages = Math.ceil(total / pageSize) || 1;
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);

    const handleSenderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSender(e.target.value);
        setPage(1);
    };

    const handleSort = (column: NetworkEmailLogSortColumn) => {
        if (column === sortBy) {
            setSortDescending(descending => !descending);
        } else {
            setSortBy(column);
            setSortDescending(column === 'date');
        }
        setPage(1);
    };

    const renderSortHeader = (column: NetworkEmailLogSortColumn, label: string) => (
        <SortButton onClick={() => handleSort(column)}>
            {label}{sortBy === column && <Glyphicon glyph={sortDescending ? 'triangle-bottom' : 'triangle-top'} />}
        </SortButton>
    );

    return (
        <Page>
            <TitleBar>
                <Title>
                    NKR Network Email Repository
                </Title>

                {centerId && (
                    <ContactLink href='https://www.kidneyregistry.com/about/contact-us/' target='_blank' rel='noopener noreferrer'>
                        Contact Us
                    </ContactLink>
                )}
            </TitleBar>

            <Card>
                <Toolbar>
                    <SearchBox>
                        <Glyphicon glyph='search' />
                        <input
                            type='text'
                            placeholder='Search by keyword, subject, or email content...'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </SearchBox>
                    <SenderSelect value={sender} onChange={handleSenderChange}>
                        <option value=''>All Senders</option>
                        {senders.map(s => <option key={s.address} value={s.address}>{s.displayName}</option>)}
                    </SenderSelect>
                </Toolbar>

                {error && <div className='text-danger'>{error}</div>}
                <LoadingIndicator type='spin' color='black' loading={loading} />

                <div style={loading ? styles.hidden : undefined}>
                    <Table>
                        <thead>
                            <tr>
                                <th style={styles.dateColumn}>{renderSortHeader('date', 'Date Sent')}</th>
                                <th style={styles.subjectColumn}>{renderSortHeader('subject', 'Subject')}</th>
                                <th style={styles.senderColumn}>{renderSortHeader('sender', 'Sent By')}</th>
                                <th style={styles.actionsColumn} />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><EmptyRow colSpan={4}>No network emails found.</EmptyRow></tr>
                            )}
                            {items.map(item => (
                                <tr key={item.id}>
                                    <td>
                                        {item.sentDate ? (
                                            <SentDate>
                                                {moment(item.sentDate).format('MMM D, YYYY')}
                                                <small>{moment(item.sentDate).format('HH:mm')} ET</small>
                                            </SentDate>
                                        ) : '-'}
                                    </td>
                                    <td><SubjectLink onClick={() => setSelectedId(item.id)}>{item.subject}</SubjectLink></td>
                                    <td style={styles.senderCell}>{item.sender}</td>
                                    <td style={styles.actionsCell}>
                                        <ViewButton onClick={() => setSelectedId(item.id)}>
                                            <Glyphicon glyph='eye-open' /> View
                                        </ViewButton>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    <Footer>
                        <span>Showing {from} to {to} of {total} emails</span>
                        <Pager>
                            <PageButton disabled={page <= 1} onClick={() => setPage(p => p - 1)}>&#8249;</PageButton>
                            {getPageNumbers(page, totalPages).map((n, i) => n === null
                                ? <Ellipsis key={`gap${i}`}>...</Ellipsis>
                                : <PageButton key={n} active={n === page} onClick={() => setPage(n)}>{n}</PageButton>
                            )}
                            <PageButton disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>&#8250;</PageButton>
                        </Pager>
                    </Footer>
                </div>
            </Card>

            {!!centerId && (
                <Button bsSize='small' style={styles.backButton} href={`/center/${centerId}/regulatory-compliance`}>
                    <Glyphicon glyph='chevron-left' /> Go Back to the Resource Hub
                </Button>
            )}

            <NetworkEmailDetailDrawer id={selectedId} centerId={centerId} onClose={() => setSelectedId(null)} />
        </Page>
    );
};

export default NetworkEmailSentList;
